import React, { useState, useEffect, useRef } from 'react';
import { injectIntl } from 'react-intl';
import {
  Avatar, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, Tooltip, Typography
} from '@mui/material';
import moment from 'moment';
import CallMadeIcon from '@mui/icons-material/CallMade';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import ReactJson from 'react-json-view';
import { vt } from '../../styles/voip-theme';
import voipService from '../../services/voip.service';

// Modal de detalhes de uma chamada VOIP — compartilhado entre o histórico global e a aba Telefonia do chamado.
// Mostra TODOS os campos + player/download da gravação + timeline (tb_voip_call_event) + JSON cru do provedor.

const fmtClock = (s) => { const t = Math.max(0, Math.floor(s || 0)); return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`; };
const fmtWhen = (iso) => (iso ? moment(iso).format('DD/MM/YYYY HH:mm') : '—');
const callNumber = (c) => (c.direction === 'inbound' ? c.fromNumber : c.toNumber) || '—';

function VoipCallDetailsModal({ intl, call, onClose }) {
  const f = (id, def) => intl.formatMessage({ id, defaultMessage: def });
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const mountedRef = useRef(true);
  const parseMeta = (m) => { try { return m ? JSON.parse(m) : null; } catch (e) { return null; } };

  useEffect(() => {
    mountedRef.current = true;
    if (!call || !call.id) { setEvents([]); return () => { mountedRef.current = false; }; }
    setLoadingEvents(true);
    voipService.getCallEvents(call.id)
      .then((res) => { if (mountedRef.current) setEvents((res && res.data) || []); })
      .catch(() => { if (mountedRef.current) setEvents([]); })
      .then(() => { if (mountedRef.current) setLoadingEvents(false); });
    return () => { mountedRef.current = false; };
  }, [call]);

  const dc = call;
  const meta = parseMeta(dc && dc.metadata);
  const fields = dc ? [
    [f('voip.calls.d.from', 'Origem'), dc.fromNumber || '—'],
    [f('voip.calls.d.to', 'Destino'), dc.toNumber || '—'],
    [f('voip.calls.d.outbound', 'Número de saída'), dc.outboundCallerId || '—'],
    [f('voip.calls.d.direction', 'Direção'), dc.direction === 'inbound' ? f('voip.calls.hist.inbound', 'Recebida') : f('voip.calls.hist.outbound', 'Realizada')],
    [f('voip.calls.d.status', 'Status'), dc.status || '—'],
    [f('voip.calls.d.modality', 'Modalidade'), dc.modality || '—'],
    [f('voip.calls.d.provider', 'Provedor'), dc.providerName || '—'],
    [f('voip.calls.d.started', 'Início'), fmtWhen(dc.startedAt)],
    [f('voip.calls.d.answered', 'Atendida em'), fmtWhen(dc.answeredAt)],
    [f('voip.calls.d.ended', 'Fim'), fmtWhen(dc.endedAt)],
    [f('voip.calls.d.duration', 'Duração'), dc.durationSeconds > 0 ? fmtClock(dc.durationSeconds) : '—'],
    [f('voip.calls.d.providerCallId', 'ID no provedor'), dc.providerCallId || '—'],
    [f('voip.calls.d.assigned', 'Agente (id)'), dc.assignedUserId || '—'],
    [f('voip.calls.d.matchedUser', 'Contato casado (id)'), dc.matchedUserId || '—'],
    [f('voip.calls.d.matchedOrg', 'Organização (id)'), dc.matchedOrganizationId || '—'],
    [f('voip.calls.d.called', 'Chamado'), dc.calledId ? `#${dc.calledId}` : '—'],
    [f('voip.calls.d.recStatus', 'Gravação (status)'), dc.recordingStatus || '—'],
    [f('voip.calls.d.transc', 'Transcrição'), dc.transcriptionStatus || '—'],
    [f('voip.calls.d.config', 'Config (id)'), dc.voipProviderConfigId || '—'],
  ] : [];

  const evLabels = { initiated: 'Discada', reconciled: 'Reconciliada', recording_ready: 'Gravação disponível', recording_attached: 'Gravação anexada', unresolved: 'Sem desfecho', inbound_detected: 'Recebida detectada', called_linked: 'Vinculada a chamado' };

  return (
    <Dialog open={Boolean(dc)} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.25, fontWeight: 700, fontSize: 17, color: vt.text }}>
        <Avatar sx={{ width: 30, height: 30, bgcolor: dc && dc.direction === 'inbound' ? vt.blueSoft : vt.greenSoft, color: dc && dc.direction === 'inbound' ? vt.blue : 'var(--mexx-green-dark, #558B2F)' }}>
          {dc && dc.direction === 'inbound' ? <CallReceivedIcon sx={{ fontSize: 16 }} /> : <CallMadeIcon sx={{ fontSize: 16 }} />}
        </Avatar>
        {f('voip.calls.d.title', 'Detalhes da chamada')}{dc ? ` · #${dc.id}` : ''}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
          <Grid container spacing={1.25}>
            {fields.map(([label, value]) => (
              <Grid item xs={6} key={label}>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, textTransform: 'uppercase' }}>{label}</Typography>
                <Typography sx={{ fontSize: 13.5, color: vt.text, wordBreak: 'break-word' }}>{value}</Typography>
              </Grid>
            ))}
          </Grid>

          {dc && dc.recordingUrl && (
            <Box sx={{ mt: 2 }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.75 }}>{f('voip.calls.d.recording', 'Gravação')}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <audio controls preload="none" style={{ flex: 1, height: 38 }} src={dc.recordingUrl} />
                <Tooltip title={f('voip.calls.d.download', 'Baixar')}>
                  <IconButton component="a" href={dc.recordingUrl} target="_blank" rel="noopener noreferrer" download sx={{ color: vt.green, bgcolor: vt.greenSoft, '&:hover': { bgcolor: 'var(--mexx-green-light, #E4F0D4)' } }}>
                    <DownloadRoundedIcon sx={{ fontSize: 19 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.75 }}>{f('voip.calls.d.timeline', 'Linha do tempo')}</Typography>
            {loadingEvents ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}><CircularProgress size={18} sx={{ color: vt.green }} /></Box>
            ) : events.length === 0 ? (
              <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.calls.d.noEvents', 'Sem eventos registrados.')}</Typography>
            ) : (
              <Box>
                {events.map((ev) => {
                  const et = ev.eventtype ?? ev.eventType ?? '';
                  const src = ev.source ?? '';
                  const when = ev.occurredat ?? ev.occurredAt;
                  return (
                    <Box key={ev.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, py: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: vt.green, mt: 0.6, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: vt.text }}>{evLabels[et] || et}</Typography>
                        <Typography sx={{ fontSize: 11, color: vt.textSoft }}>{fmtWhen(when)} · {src}</Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: vt.muted, letterSpacing: 0.4, textTransform: 'uppercase', mb: 0.75 }}>{f('voip.calls.d.raw', 'Dados brutos do provedor')}</Typography>
            {meta ? (
              <Box sx={{ border: `1px solid ${vt.border}`, borderRadius: 2, p: 1, bgcolor: '#FAFBFC', overflowX: 'auto' }}>
                <ReactJson src={meta} name={false} collapsed={1} enableClipboard displayDataTypes={false} displayObjectSize={false} style={{ fontSize: 12, background: 'transparent' }} />
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: vt.textSoft }}>{f('voip.calls.d.noRaw', 'Sem dados brutos ainda (a chamada é reconciliada pelo poll — rode o reconcile ou aguarde o tick).')}</Typography>
            )}
          </Box>
        </div>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ background: vt.greenGrad, color: '#fff', fontWeight: 700, textTransform: 'none', px: 2.5, '&:hover': { background: vt.greenGradHover } }}>{f('voip.calls.d.close', 'Fechar')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default injectIntl(VoipCallDetailsModal);
